import React from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { EmptyState } from '@chatsdet/ui';
import { Sparkle } from '@phosphor-icons/react';
import { useAuth } from '../../../contexts/AuthContext';
import { useBreadcrumbs } from '../../../contexts/BreadcrumbsContext';
import { type PromptDetail } from '../api';
import { PromptRail } from '../components/PromptRail';
import { PromptViewer } from '../components/PromptViewer';
import { PromptEditorDialog } from '../components/PromptEditorDialog';

/**
 * Agent tools → Prompt Library. A master–detail workspace (left rail of prompts
 * + a big viewer) styled after the Flow Designer / Sandbox detail views. The
 * open prompt is driven by the `/prompt-library/$promptId` route param so the
 * view is deep-linkable. Org members author prompts in-app (shared with their
 * org); the same library is available from the CLI via `cofactor prompts`.
 */
export function PromptLibraryPage() {
  const { user } = useAuth();
  const isSuperadmin = user?.isSuperadmin === true;
  const navigate = useNavigate();

  // Optional param: present on `/prompt-library/$promptId`, absent on the index.
  const { promptId } = useParams({ strict: false }) as { promptId?: string };

  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<PromptDetail | null>(null);

  useBreadcrumbs([{ key: 'prompt-library', label: 'Prompt Library' }]);

  // A viewer may mutate a prompt if it's their org's, or they're a superadmin.
  const canMutate = (p: PromptDetail) => p.scope === 'org' || isSuperadmin;

  const openCreate = () => {
    setEditing(null);
    setEditorOpen(true);
  };

  const openEdit = (prompt: PromptDetail) => {
    setEditing(prompt);
    setEditorOpen(true);
  };

  const handleDeleted = () => {
    void navigate({ to: '/prompt-library' });
  };

  return (
    <div className="flex h-[calc(100vh-4rem)]">
      <PromptRail selectedId={promptId} onNew={openCreate} />

      <div className="relative flex min-w-0 flex-1 flex-col overflow-hidden">
        {promptId ? (
          <PromptViewer
            key={promptId}
            promptId={promptId}
            canMutate={canMutate}
            onEdit={openEdit}
            onDeleted={handleDeleted}
          />
        ) : (
          <div className="flex h-full items-center justify-center p-8">
            <EmptyState
              icon={<Sparkle size={24} />}
              title="Select a prompt"
              description="Choose a prompt from the list to view it, or create a new one."
            />
          </div>
        )}
      </div>

      <PromptEditorDialog
        isOpen={editorOpen}
        onOpenChange={setEditorOpen}
        editing={editing}
        canCreateGlobal={isSuperadmin}
      />
    </div>
  );
}
